import axios from "axios";
import { disablePageScroll, enablePageScroll } from 'scroll-lock';
import { createFocusTrap } from 'focus-trap';
import svg from '../../images/icons/icon--close.svg';

export default class ModalBlock {
    /**
     *
     * @param {Object} options The options to override
     * @param {NodeList | String} options.modalBlocks Either a list of the modal blocks, or a string so we can get
     * them with a document query
     * @param {String} options.modalButtons The class of the button that will open the corresponding button.
     * @param {Function} options.afterFunction A function that runs after we retrieve the modal.
     */
    constructor(options = {}) {
        this.options = {
            modalBlocks: options.modalBlocks ?? '.layered-callout__content',
            modalButtons: options.modalButtons ?? '.modal-btn',
            afterFunction: options.afterFunction ?? function() {

            }
        };

        console.log('modal-block.js - Modal Block loaded with options', this.options);

        this.modalOpen = false;
        this.isOpen = false;
        //if awaiting result from axios, to prevent us trying to load multiple modals.
        this.loading = false;
        //the currently opened modal.
        this.modal = undefined;

        this.focus = null;

        if(NodeList.prototype.isPrototypeOf(this.options.modalBlocks)) {
            this.modalBlocks = this.options.modalBlocks;
        } else if(typeof this.options.modalBlocks === 'string') {
            this.modalBlocks = document.querySelectorAll(this.options.modalBlocks);
        } else {
            throw new Error('modal-block.js - Invalid type used for modalBlocks');
        }

        if(this.modalBlocks && this.modalBlocks.length !== 0) {
            this.initialiseModalBlock()
        } else {
            console.warn('modal-block.js - No modals found on this page, if this is intended ignore this warning');
        }
    }

    initialiseModalBlock() {
        Array.from(this.modalBlocks).forEach(modalBlock => {
            if(typeof this.options.modalButtons !== 'string') {
                throw new Error('modal-block.js - modalButtons must be of type String!');
            }

            let btns = modalBlock.querySelectorAll(this.options.modalButtons);

            Array.from(btns).forEach((btn, i) => {
                btn.ariaPressed = false;                    

                //ensuring the button has the necessary dataset variables.
                if(btn.dataset.modalPost && btn.dataset.modalPostType && btn.dataset.modalShowArrows) {
                    btn.addEventListener('click', e => {
                        btn.ariaPressed = true;
                        this.openModel(btn, modalBlock, parseInt(btn.dataset.modalPost), btn.dataset.modalPostType, this.initialiseArrows(btn, i, btns.length, btns, modalBlock), btn.dataset.modalUseInline);
                    })
                } else {
                    throw new Error('modal-block.js - Modal buttons must contain data-modal-post, data-modal-post-type and data-modal-show-arrows');
                }
            })

        })
    }

    /**
     *
     * @param {HTMLElement} btn The button to check should show arrows
     * @param {Number} i The index of the button
     * @param {Number} totalButtons The total number of buttons in the row.
     * @param {NodeList} btns All the buttons in the row
     * @param {HTMLElement} container The buttons container row.
     * @return {HTMLElement | null}
     */
    initialiseArrows(btn, i, totalButtons, btns, container) {
        let arrowsContainer = null;
        //if we are to show the arrow buttons do it here.
        if(btn.dataset.modalShowArrows === 'true') {
            arrowsContainer = document.createElement('DIV');
            arrowsContainer.classList.add('modal__arrows-wrapper', 'options--scrollbar', 'options--mobile-full-width');
            let arrows = document.createElement('DIV');
            arrows.classList.add('modal__arrows', 'grid', 'justify-space-between', 'align-center');
            let leftArrow = document.createElement('BUTTON');
            leftArrow.classList.add('arrow-btn', 'arrow-btn--prev');
            leftArrow.innerText = "←"
            leftArrow.ariaPressed = false;
            arrows.appendChild(leftArrow)
            let progress = document.createElement('DIV');
            progress.classList.add('modal__progress');
            progress.innerText = `${i + 1} of ${totalButtons}`
            arrows.appendChild(progress);
            let rightArrow = document.createElement('BUTTON');
            rightArrow.classList.add('arrow-btn', 'arrow-btn--next');
            rightArrow.innerText = "→"
            rightArrow.ariaPressed = false;
            arrows.appendChild(rightArrow)
            arrowsContainer.appendChild(arrows);

            leftArrow.addEventListener('click', e => {
                if(!this.loading) {
                    leftArrow.ariaPressed = true;
                    let temp = i - 1;
                    if (temp < 0) {
                        temp = totalButtons - 1;
                    }

                    let btnIndex = temp;

                    let newBtn = Array.from(btns)[btnIndex];
                    let modalPost = parseInt(newBtn.dataset.modalPost)
                    let modalPostType = newBtn.dataset.modalPostType;
                    this.updateModal(newBtn, container, modalPost, modalPostType, this.initialiseArrows(newBtn, temp, totalButtons, btns, container));
                }
            });

            rightArrow.addEventListener('click', e => {
                if(!this.loading) {
                    rightArrow.ariaPressed = true;
                    let temp = i + 1;
                    if (temp > totalButtons - 1) {
                        temp = 0;
                    }

                    let btnIndex = temp;

                    let newBtn = Array.from(btns)[btnIndex];
                    let modalPost = parseInt(newBtn.dataset.modalPost)
                    let modalPostType = newBtn.dataset.modalPostType;
                    this.updateModal(newBtn, container, modalPost, modalPostType, this.initialiseArrows(newBtn, temp, totalButtons, btns, container));
                }
            })
        }
        
        return arrowsContainer;
    }

    /**
     * When the modal button is clicked open a modal.
     * @param {HTMLElement} btn The button that was clicked
     * @param {HTMLElement} container The modal block that this button belongs to.
     * @param {Number} modalId the ID of the post to display on the modal
     * @param {String} modalPostType The post type this post belongs to.
     * @param {HTMLElement | null} arrows The buttons to add if they are to be added
     * @
     */
    openModel(btn, container, modalId, modalPostType, arrows, useInline) {
        if(!this.modalOpen && !this.loading) {
            // console.log('modal-block.js - Modal opening.');

            //if the modal is somehow open at this point, close it.
            if(this.modal) {
                this.modal.remove();
            }

            //checking if we have a modal, if we don't create an empty one, if we do then use this.
            let modal = document.querySelector('.modal');
            //the pages main tag
            let main = document.querySelector('main');

            if(!main) {
                throw new Error('modal-block.js - no <main> tag present on this page, this is required for the modal');
            }

            if(!modal) {
                modal = document.createElement('ARTICLE');
                modal.classList.add('modal');
                main.appendChild(modal);
                this.modal = modal;
            }

            disablePageScroll(this.modal)

            this.updateModal(btn, container, modalId, modalPostType, arrows, useInline);
        }
    }

    /**
     * When the modal button is clicked open a modal.
     * @param {HTMLElement} btn The button that was clicked
     * @param {HTMLElement} container The modal block that this button belongs to.
     * @param {Number} modalId the ID of the post to display on the modal
     * @param {String} modalPostType The post type this post belongs to.
     * @param {HTMLElement | null} arrows The buttons to add if they are to be added
     * @
     */
    updateModal(btn, container, modalId, modalPostType, arrows, useInline) {
        if(this.focus) {
            this.focus.deactivate();
            this.focus = null;
        }

        this.focus = createFocusTrap(this.modal);

        //ensuring the posttype and id are of the correct type.
        // if(typeof modalId !== 'number' || typeof modalPostType !== 'string') {
        //     throw new Error('modal-block.js - data-modal-post must be of type number and data-modal-post-type must be of type string');
        // }

        let modalContainer = this.modal.querySelector('.modal__container');
        //emptying the modal content if it exists.
        if(!modalContainer) {
            modalContainer = document.createElement('SECTION');
            modalContainer.classList.add('modal__container');
            if(!this.isOpen) {
                // modalContainer.classList.add( 'animate__animated', 'animate__modalSlide');
                // modalContainer.style.backgroundPosition = "left";
            }
            modalContainer.ariaModal = true;
            modalContainer.setAttribute('role', 'dialog')
            modalContainer.setAttribute('aria-labelledby', `${modalPostType} Content`);
        }

        let closeBtn = modalContainer.querySelector('.btn--close');

        if(!closeBtn) {
            closeBtn = document.createElement('BUTTON');
            closeBtn.classList.add('btn--close');
            closeBtn.innerHTML = "<img src='/wp-content/themes/class/dist/images/icons/icon--close.svg' />";
            closeBtn.addEventListener('click', e => {
                this.closeModal();
            })
            modalContainer.appendChild(closeBtn);
        }

        let modalContent = modalContainer.querySelector('.modal__content');

        if(!modalContent) {
            modalContent = document.createElement('DIV');
            modalContent.classList.add('modal__content');
            modalContainer.appendChild(modalContent);
        }

        let placeholder = `
                <div class="placeholder placeholder--title"></div>
                <div class="placeholder placeholder--image"></div>
            `

        let contentLines =  Math.round(Math.random() * (5 - 3) + 3);

        for(let i = 0; i < contentLines; i++) {
            let min = i === contentLines - 1 ? 20 : 92;
            let max = i === contentLines - 1 ? 50 : 100;
            let rand = Math.round(Math.random() * (max - min) + min);
            placeholder += `<div class="placeholder placeholder--content" style="width: ${rand}%"></div>`;
        }

        modalContent.innerHTML = placeholder

        //if arrows already exist, remove them.
        let tempArrows = modalContainer.querySelector('.modal__arrows');
        if(tempArrows) {
            tempArrows.remove();
        }

        if(arrows) {
            modalContainer.appendChild(arrows);
        }

        if(!this.modal.querySelector('modal__container')) {
            this.modal.appendChild(modalContainer);
        }

        this.modal.removeEventListener('click', e => {
            if(e.target === this.modal) {
                this.closeModal();
            }
        });

        //if we click on the modal itself, and not the content then close the modal.
        this.modal.addEventListener('click', e => {
            if(e.target === this.modal) {
                this.closeModal();
            }
        });

        let escape = (e) => {
            if(e.key === 'Escape') {
                this.closeModal();
            }
        }

        document.removeEventListener('keydown', e => {escape(e)})
        document.addEventListener('keydown', e => {escape(e)})

        this.loading = true;

        if(!this.isOpen) {
            window.setTimeout(() => {
                modalContainer.style.backgroundPosition = "right";
                modalContent.style.opacity = "1";
                if(arrows) {
                    arrows.style.opacity = "1";
                    arrows.style.transition = "opacity .2s linear .3s";
                }
            }, 10);
        } else {
            if(arrows) {
                arrows.style.opacity = "1";
            }
        }

        // If modalId is a queryselector then use that to get the post, otherwise use axios to get the post
        if(typeof useInline !== 'undefined') {
            let modalContentFromPage = document.querySelector(useInline);
            if(modalContentFromPage) {
                this.isOpen = true;
                this.modal.querySelector('.modal__content').innerHTML = modalContentFromPage.innerHTML;
                this.options.afterFunction();
                this.reactivateButtons(btn)
                if(this.focus) {
                    this.focus.activate();
                }
                this.loading = false;
                this.modalOpen = true;

                if(this.modal) {
                    if(modalContainer.classList.contains('animate__animated')) {
                        window.setTimeout(() => {
                            // modalContainer.classList.remove('animate__animated', 'animate__slideInRight');
                        }, 2000)
                    }
                }
                return;
            } else {
                console.warn('modal-block.js - useInline selector did not return any content, falling back to axios call');
            }
        }

        axios.post(window.location.origin + '/wp-json/queries/modal', {
            postType: modalPostType,
            modalId: modalId,
        }).then(res => {
            // console.log("modal-block.js - Received ", res)
            this.isOpen = true;
            this.modal.querySelector('.modal__content').innerHTML = res.data.html;
            let anchors = this.modal.querySelectorAll('a[href^="#"]');
            Array.from(anchors).forEach(anchor => {
                anchor.addEventListener('click', function (e) {
                    e.preventDefault();

                    document.querySelector(this.getAttribute('href')).scrollIntoView({
                        behavior: 'smooth'
                    });
                });
            });
            this.options.afterFunction();
        }).finally(() => {
            this.reactivateButtons(btn)
            if(this.focus) {
                this.focus.activate();
            }
            this.loading = false;
            this.modalOpen = true;

            if(this.modal) {
                if(modalContainer.classList.contains('animate__animated')) {
                    window.setTimeout(() => {
                        // modalContainer.classList.remove('animate__animated', 'animate__slideInRight');
                    }, 2000)
                }
            }
        }).catch(e => {
            this.reactivateButtons(btn)
            this.loading = false;
            console.log("modal-block.js - Error getting the post " + modalId + " escaping with error", e);
        })
    }

    /** Removing the modal from the screen and ensuring it can be re-enabled on the next press. */
    closeModal() {
        this.modal.remove();
        this.loading = false;
        this.modalOpen = false;
        this.isOpen = false;
        if(this.focus) {
            this.focus.deactivate();
            this.focus = null;
        }
        enablePageScroll(this.modal);
    }

    /** Ensure aria-pressed on the buttons is false
     * @param {HTMLElement} The modal-btn responsible for opening this modal. */
    reactivateButtons(btn) {
        if(btn.ariaPressed === "true") {
            btn.ariaPressed = false;
        }

        let leftArrowBtn = this.modal.querySelector('.arrow-btn__prev');
        let rightArrowBtn = this.modal.querySelector('.arrow-btn__next');

        if(leftArrowBtn && leftArrowBtn.ariaPressed === "true") {
            leftArrowBtn.ariaPressed = false;
        }

        if(rightArrowBtn && rightArrowBtn.ariaPressed == "true") {
            rightArrowBtn.ariaPressed = true;
        }
    }
}